import { Box, Button, Divider, Stack, type SxProps, Typography } from '@mui/material';
import Drawing from '../../drawing';
import Popup from '../../popup';

interface BaseProps {
    open: boolean;
    variant?: 'success' | 'children';
    setClose?: () => void;
    onGoBack?: () => void;
    warning?: string;
    title: string;
    buttonVariant?: 'text' | 'contained' | 'outlined';
    buttonText?: React.ReactNode;
    handleClick?: () => void;
    secondaryButtonText?: React.ReactNode;
    secondaryButtonVariant?: 'text' | 'contained' | 'outlined';
    handleClickSecondary?: () => void;
    disabled?: boolean;
    buttonDataTestId?: string;
    containerChildrenSx?: SxProps;
    dataTestId?: string;
}

type BayerModalProps = BaseProps &
    (
        | {
              variant: 'children';
              children: React.ReactNode;
              description?: never;
              crop?: never;
              containerChildrenSx?: SxProps;
          }
        | {
              variant?: 'success';
              children?: never;
              description: string;
              crop?: string;
          }
    );

const BayerModal = ({
    open,
    variant = 'success',
    setClose,
    onGoBack,
    warning,
    title,
    buttonVariant = 'contained',
    buttonText,
    handleClick,
    secondaryButtonVariant = 'text',
    secondaryButtonText,
    handleClickSecondary,
    disabled,
    description,
    crop,
    children,
    containerChildrenSx,
    buttonDataTestId,
    dataTestId,
}: BayerModalProps) => {
    return (
        <Popup
            showCloseIcon={!!setClose}
            onGoBack={onGoBack}
            showBackIcon={!!onGoBack}
            disablePadding
            visible={open}
            onClose={setClose}
            title={variant === 'children' && title ? title : undefined}
            dataTestId={dataTestId}
        >
            {variant === 'children' && title && <Divider sx={{ width: '100%', pt: 1 }} />}
            <Stack justifyContent={'center'} spacing={3} sx={{ mt: 4 }} alignItems={'center'}>
                {variant === 'children' ? (
                    <Box sx={{ maxHeight: '75vh', overflow: 'auto', ...containerChildrenSx }}>{children}</Box>
                ) : (
                    <>
                        {variant === 'success' && (
                            <Drawing type={crop ? `success-${crop}` : 'success-corn'} size='sm' />
                        )}
                        <Stack spacing={2} sx={{ px: 2 }}>
                            {variant === 'success' && (
                                <Typography color='primary' align='center' variant='h6' sx={{ fontWeight: 500 }}>
                                    {title}
                                </Typography>
                            )}
                            {description && (
                                <Typography variant='body1' color='primary' align={'center'}>
                                    {description}
                                </Typography>
                            )}
                            {warning && (
                                <Typography variant='body1' color='primary' align={'center'}>
                                    {warning}
                                </Typography>
                            )}
                        </Stack>
                    </>
                )}
                {variant === 'children' && <Divider sx={{ width: '100%' }} />}
            </Stack>

            {!secondaryButtonText && !buttonText ? (
                ''
            ) : (
                <Stack
                    justifyContent={'flex-end'}
                    direction='row'
                    spacing={2}
                    alignItems={'end'}
                    mt={1.5}
                    sx={{ px: 2, pb: 2 }}
                >
                    {!!secondaryButtonText && (
                        <Button
                            variant={secondaryButtonVariant}
                            color='secondary'
                            onClick={handleClickSecondary}
                            sx={{ borderRadius: 0 }}
                            disabled={disabled}
                        >
                            <Typography fontWeight={600} variant='caption'>
                                {secondaryButtonText}
                            </Typography>
                        </Button>
                    )}
                    {!!buttonText && (
                        <Button
                            variant={buttonVariant}
                            color='secondary'
                            onClick={handleClick}
                            sx={{ borderRadius: 0 }}
                            disabled={disabled}
                            data-testid={buttonDataTestId}
                        >
                            <Typography
                                variant='caption'
                                sx={{
                                    fontWeight: 600,
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                {buttonText}
                            </Typography>
                        </Button>
                    )}
                </Stack>
            )}
        </Popup>
    );
};

export default BayerModal;
